/**
 * Shared frontend utilities — port of legacy/js/utils.js.
 *
 * Pure functions only. The SVG arrow markup that lived here in vanilla now
 * lives as JSX components in src/components/Arrow.tsx.
 */

import type { Coords, CompassDir, EnrichedSpot, Level, SpeedUnit } from "./types";

// ─── Unit conversion ──────────────────────────────────────────────────────────

const CONV: Record<SpeedUnit, number> = {
  kn: 1.944,
  ms: 1,
  kmh: 3.6,
  mph: 2.237,
};

export const UNIT_LABELS: Record<SpeedUnit, string> = {
  kn: "kn",
  ms: "m/s",
  kmh: "km/h",
  mph: "mph",
};

/** Format m/s to a given unit, rounded integer. */
export function fmtSpeed(speedMs: number, unit: SpeedUnit): string {
  return String(Math.round(speedMs * CONV[unit]));
}

/**
 * Format a min–max range (m/s) to the given unit. Uses the U+2011 non-breaking
 * hyphen so the range never wraps mid-pair.
 */
export function fmtSpeedRange(minMs: number, maxMs: number, unit: SpeedUnit): string {
  const lo = Math.round(minMs * CONV[unit]);
  const hi = Math.round(maxMs * CONV[unit]);
  return lo === hi ? `${lo}` : `${lo}‑${hi}`;
}

// ─── Score levels ─────────────────────────────────────────────────────────────

export interface LevelMeta {
  label: string;
  color: string;
}

export const SCORE_LEVELS: Record<Exclude<Level, "skip">, LevelMeta> = {
  prime: { label: "Prime", color: "#00FF55" },
  good: { label: "Good", color: "#14A5FF" },
  marginal: { label: "Marginal", color: "#FFFF00" },
  unlikely: { label: "Unlikely", color: "#FF8800" },
};

export function scoreLevel(score: number): Exclude<Level, "skip"> {
  if (score >= 75) return "prime";
  if (score >= 50) return "good";
  if (score >= 20) return "marginal";
  return "unlikely";
}

// ─── Wind speed colour scale (knots) ─────────────────────────────────────────
// 10 stops from the Figma variable panel. < 4 kn returns null so the caller
// can fall back to a dim foreground colour.

type ColourStop = readonly [knots: number, hex: string];

const KNOT_STOPS: readonly ColourStop[] = [
  [0, "#000000"],
  [4, "#93C0FF"],
  [7, "#3087FF"],
  [11, "#00F6FF"],
  [17, "#00FF44"],
  [22, "#FFAE00"],
  [28, "#FF4D00"],
  [34, "#FF0000"],
  [41, "#FF00BF"],
  [56, "#8708FF"],
  [70, "#8708FF"],
];

function hexToRgb(hex: string): [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

/**
 * Returns an interpolated `rgb(...)` string for a given m/s speed, or null if
 * the wind is below 4 kn (the vanilla design shows fg-dimmer in that case).
 */
export function windColor(speedMs: number): string | null {
  const kn = speedMs * 1.944;
  if (kn < 4) return null;

  const clamped = Math.min(kn, 70);
  // Find the bracket [lo, hi] where stops[lo].kn <= clamped < stops[hi].kn.
  let loIdx = KNOT_STOPS.length - 2;
  for (let i = 0; i < KNOT_STOPS.length - 1; i++) {
    const cur = KNOT_STOPS[i];
    const nxt = KNOT_STOPS[i + 1];
    if (!cur || !nxt) continue;
    if (clamped >= cur[0] && clamped < nxt[0]) {
      loIdx = i;
      break;
    }
  }
  const lo = KNOT_STOPS[loIdx];
  const hi = KNOT_STOPS[loIdx + 1];
  if (!lo || !hi) return null; // unreachable given the clamp, here for the type narrowing

  const t = (clamped - lo[0]) / (hi[0] - lo[0]);
  const [r1, g1, b1] = hexToRgb(lo[1]);
  const [r2, g2, b2] = hexToRgb(hi[1]);
  return `rgb(${Math.round(r1 + (r2 - r1) * t)},${Math.round(g1 + (g2 - g1) * t)},${Math.round(b1 + (b2 - b1) * t)})`;
}

export function windColorWithFallback(speedMs: number, fallback = "var(--foreground-muted)"): string {
  return windColor(speedMs) ?? fallback;
}

// ─── Compass ──────────────────────────────────────────────────────────────────

const COMPASS_DEG: Record<CompassDir, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

export function compassToDeg(dir: CompassDir): number {
  return COMPASS_DEG[dir];
}

// ─── Day labels ───────────────────────────────────────────────────────────────

const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Abbreviated weekday name from a "YYYY-MM-DD" string. */
export function dayName(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  return SHORT_DAYS[d.getDay()] ?? "";
}

/** Day-of-month number from "YYYY-MM-DD". */
export function dayNum(dateStr: string): number {
  return Number.parseInt(dateStr.slice(8, 10), 10);
}

/**
 * Composite ranking key for a spot on a given forecast day: the
 * day-summary score (`days[i].score`) primary, the day's peak hour
 * speed secondary. The scorer's per-day output is coarse — many days
 * land on the same bucket value (e.g. 55 for "direction-prime,
 * speed-below-ideal") — so without the speed tiebreaker the spot's
 * "best day" picker silently collapses to the earliest tied day, even
 * if a later day has visibly more wind. Returns `[0, 0]` for skip /
 * no-data days so they sort lowest deterministically.
 */
export type PeakKey = readonly [score: number, peakSpeedMs: number];

export function peakHourKey(spot: EnrichedSpot, dayIndex: number): PeakKey {
  const day = spot.days[dayIndex];
  if (!day || day.level === "skip") return [0, 0];
  let bestSpeed = 0;
  for (const h of day.hours) {
    if (h.speedMs > bestSpeed) bestSpeed = h.speedMs;
  }
  return [day.score, bestSpeed];
}

/** Lexicographic compare for two `PeakKey`s. Higher score wins; ties
 *  break by higher peak speed. Returns the signed delta suitable for
 *  `Array.sort` callbacks. */
export function comparePeakKey(a: PeakKey, b: PeakKey): number {
  if (a[0] !== b[0]) return b[0] - a[0];
  return b[1] - a[1];
}


/** Short label for a forecast day: "Today" / "Tomorrow" / "Fri" etc. */
export function shortDayLabel(dayOffset: number, dateStr: string): string {
  if (dayOffset === 0) return "Today";
  if (dayOffset === 1) return "Tomorrow";
  return dayName(dateStr);
}

const FULL_DAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
] as const;

/**
 * Section-header / dropdown-label day phrase. Returns "today",
 * "tomorrow", or "Wednesday 9th" — used by the spot-overview filter to
 * label sections like "Best Wednesday 9th" or the dynamic dropdown
 * "Best wind today / tomorrow / Friday 22nd".
 */
export function longDayLabel(dayOffset: number, dateStr: string): string {
  if (dayOffset === 0) return "today";
  if (dayOffset === 1) return "tomorrow";
  const d = new Date(`${dateStr}T12:00:00`);
  const dom = d.getDate();
  const name = FULL_DAYS[d.getDay()] ?? "";
  return `${name} ${dom}${ordinalSuffix(dom)}`;
}

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function ordinalSuffix(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

/**
 * Subtitle for the detail-page navbar: a relative day label plus the
 * hour of the selected forecast, e.g. "Today · 12.00", "Tomorrow · 14.00",
 * "Sep 28th · 09.00". `time` is the ISO timestamp from a WindHour/ScoredHour.
 */
export function navbarHourLabel(time: string): string {
  // "YYYY-MM-DDTHH:MM:SS" → "HH.MM"
  const timeStr = time.slice(11, 16).replace(":", ".");
  const dateStr = time.slice(0, 10);
  // Compare date portions in local time. Build today's "YYYY-MM-DD".
  const now = new Date();
  const todayY = now.getFullYear();
  const todayM = String(now.getMonth() + 1).padStart(2, "0");
  const todayD = String(now.getDate()).padStart(2, "0");
  const todayStr = `${todayY}-${todayM}-${todayD}`;
  if (dateStr === todayStr) return `Today · ${timeStr}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomY = tomorrow.getFullYear();
  const tomM = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const tomD = String(tomorrow.getDate()).padStart(2, "0");
  if (dateStr === `${tomY}-${tomM}-${tomD}`) return `Tomorrow · ${timeStr}`;
  const d = new Date(`${dateStr}T12:00:00`);
  const dom = d.getDate();
  return `${SHORT_MONTHS[d.getMonth()]} ${dom}${ordinalSuffix(dom)} · ${timeStr}`;
}

// ─── Chart-specific colour & sizing helpers ────────────────────────────────
// Used by the hourly bar chart on the detail page. Different stop set than
// `windColor` above (chart stops are a separate Figma scale tuned for the bar
// chart's larger range).

type ChartStop = readonly [knots: number, r: number, g: number, b: number];

/* Wind-speed → bar colour. Tuned for wingfoiling / kitesurfing: the
   sweet-spot 15–25 kn band is a flat #00FF55 plateau (matches the
   `--wind-direction-prime-default` token, so a spot whose direction
   *and* speed both work shows one coherent green) — the marker arc
   reads as a bold "green = go" across the rideable range. Below 15 kn
   the sweep moves blue → teal → green so the ramp-up tells the user
   "almost there". Above 25 kn the gradient turns over quickly through
   lime → orange → red, signalling "ring is full but overpowered" via
   colour while the arc length itself stays at 100%.
   Hours below 3 kn render `--wind-nil` in HourlyChart; the stop at 3
   is the floor the gradient interpolates up from. */
const CHART_KNOT_STOPS: readonly ChartStop[] = [
  [3,  255, 255, 255], // #FFFFFF — calm
  [5,  179, 210, 255], // #B3D2FF — light blue
  [10, 11,  125, 255], // #0B7DFF — medium blue
  [13, 0,   254, 229], // #00FEE5 — teal (brief transition)
  [15, 0,   255, 85],  // #00FF55 — bright green (sweet spot begins)
  [25, 0,   255, 85],  // #00FF55 — bright green (sweet spot ends)
  [28, 212, 255, 0],   // #D4FF00 — lime / yellow-green (early overpowered)
  [32, 255, 174, 0],   // #FFAE00 — orange (overpowered)
  [42, 255, 77,  0],   // #FF4D00 — red-orange
  [55, 255, 0,   0],   // #FF0000 — red
  [64, 135, 8,   255], // #8708FF — purple (extreme)
];

/**
 * Map-marker arc fill ratio. Encodes wind speed (knots) into 0..1 around
 * the marker's outer ring. Two-segment linear curve:
 *
 *   • 0–10 kn → 0–25% (light wind, slow ramp — at 10 kn we're a quarter
 *     full). A 5% floor keeps a visible sliver at dead calm so the
 *     marker doesn't read as "no data".
 *   • 10–25 kn → 25–100% (the meaty band — slope doubles so the user
 *     reads progress quickly through the rideable range). 15 kn lands
 *     at 50%, 20 kn at 75%, 25 kn closes the ring.
 *   • 25+ kn → clamped at 100%. The arc's *colour* (via `knotsColor`)
 *     already shifts to lime / orange / red / purple in this range, so
 *     the ring being maxed-out + warm-coloured signals "rideable +
 *     overpowered" without a second indicator.
 *
 * The background track in the marker SVG fills the rest of the ring at
 * low alpha so the active arc always reads as a fraction of a known
 * total instead of as an unmoored stroke.
 */
export function speedArcRatio(speedKn: number): number {
  if (speedKn >= 25) return 1;
  if (speedKn >= 10) return 0.25 + ((speedKn - 10) / 15) * 0.75;
  return Math.max(0.05, (speedKn / 10) * 0.25);
}

/** Hourly-chart bar colour. Returns rgb() interpolated between Figma stops. */
export function knotsColor(kn: number): string {
  const s = CHART_KNOT_STOPS;
  const first = s[0];
  const last = s[s.length - 1];
  if (!first || !last) return "rgb(255,255,255)";
  if (kn <= first[0]) return `rgb(${first[1]},${first[2]},${first[3]})`;
  if (kn >= last[0]) return `rgb(${last[1]},${last[2]},${last[3]})`;
  for (let i = 1; i < s.length; i++) {
    const cur = s[i];
    const prev = s[i - 1];
    if (!cur || !prev) continue;
    if (kn <= cur[0]) {
      const t = (kn - prev[0]) / (cur[0] - prev[0]);
      const r = Math.round(prev[1] + t * (cur[1] - prev[1]));
      const g = Math.round(prev[2] + t * (cur[2] - prev[2]));
      const b = Math.round(prev[3] + t * (cur[3] - prev[3]));
      return `rgb(${r},${g},${b})`;
    }
  }
  return `rgb(${last[1]},${last[2]},${last[3]})`;
}

/**
 * Bar height in pixels, given knots. Piecewise-linear between named anchors
 * tuned for wingfoiling / kitesurfing:
 *   • 0–3 kn — very short bars (--wind-nil paints them grey anyway)
 *   • 5–20 kn — steepest slope (≈ 4 px/kn) so the rideable window
 *     dominates vertical real estate. h(20) is exactly 2 × h(10).
 *   • 25–30 kn — slightly shallower (≈ 3 px/kn), still readable
 *   • 30–64 kn — heavily compressed tail. 50 and 64 differ visibly but
 *     not dramatically; the chart shouldn't be dominated by storm bars.
 * Grid mirrors CHART_KNOT_STOPS so colour transitions and height
 * transitions snap to the same knot values.
 */
const CHART_HEIGHT_STOPS: ReadonlyArray<readonly [knots: number, px: number]> = [
  [3, 6],
  [5, 16],
  [10, 35],
  [15, 55],
  [20, 70],
  [25, 88],
  [30, 102],
  [40, 118],
  [50, 124],
  [64, 130],
];

export function barHeightPx(speedKn: number): number {
  if (speedKn <= 0) return 4;
  if (speedKn <= 3) return Math.round(4 + (speedKn / 3) * 2);
  const s = CHART_HEIGHT_STOPS;
  const last = s[s.length - 1];
  if (!last) return 4;
  if (speedKn >= last[0]) return last[1];
  for (let i = 1; i < s.length; i++) {
    const cur = s[i];
    const prev = s[i - 1];
    if (!cur || !prev) continue;
    if (speedKn <= cur[0]) {
      const t = (speedKn - prev[0]) / (cur[0] - prev[0]);
      return Math.round(prev[1] + t * (cur[1] - prev[1]));
    }
  }
  return last[1];
}

/**
 * Score level → CSS variable for accent colour.
 *
 * NOTE (2026-05-10): no longer used for wind direction arrows — those moved
 * to `dirLevelColor()` so arrow colour reflects pure direction match,
 * independent of speed. `level` is still used for **spot ranking** and
 * **day-strip summary tinting** where the combined direction-and-speed score
 * makes sense (it answers "is this day worth checking at all?"). See
 * `docs/decisions.md` for rationale.
 */
export function levelColor(level: Level): string {
  if (level === "prime") return "var(--wind-direction-prime-default)";
  if (level === "good") return "var(--wind-direction-good-default)";
  if (level === "marginal") return "var(--wind-direction-marginal-default)";
  if (level === "unlikely") return "var(--wind-direction-unlikely-default)";
  return "var(--foreground-muted)"; // skip
}

// ─── Direction-only colour ───────────────────────────────────────────────────

const DIR_DEG: Record<CompassDir, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

function angleDiffDeg(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Direction-only accent colour for wind arrows. **Independent of wind speed**
 * — speed is communicated separately by bar/blob colour (`knotsColor`).
 *
 * Four tiers, derived from the angular distance from `dirDeg` to the
 * nearest direction in `idealDirs`:
 *
 *   • prime green   — ≤ 22.5°  (forecast direction sits in an ideal cell)
 *   • good teal     — ≤ 45°    (one compass cell out; absorbs forecast
 *                                tolerance — real wind often lands inside
 *                                the ideal range anyway)
 *   • unlikely orange — ≥ 157.5° (within ±22.5° of the opposite of any
 *                                  ideal direction → offshore)
 *   • neutral       — otherwise (mid-quadrant: sailable in principle,
 *                                just not what this spot is set up for)
 *
 * This is the canonical arrow colour function across the app: hourly-chart
 * bars, hourly-forecast-card hero arrow, day-strip arrow, and the
 * spot-detail compass all read from this so the user sees one consistent
 * meaning for every arrow tint they encounter.
 */
export function dirLevelColor(
  dirDeg: number,
  idealDirs: readonly CompassDir[],
): string {
  if (idealDirs.length === 0) {
    return "var(--wind-direction-neutral-default)";
  }
  let minDelta = 180;
  for (const d of idealDirs) {
    const delta = angleDiffDeg(dirDeg, DIR_DEG[d]);
    if (delta < minDelta) minDelta = delta;
  }
  if (minDelta <= 22.5) return "var(--wind-direction-prime-default)";
  if (minDelta <= 45) return "var(--wind-direction-good-default)";
  if (minDelta >= 157.5) return "var(--wind-direction-unlikely-default)";
  return "var(--wind-direction-neutral-default)";
}

// ─── Geo ────────────────────────────────────────────────────────────────────

/** Great-circle distance between two coordinates, in kilometres (haversine). */
export function distanceKm(a: Coords, b: Coords): number {
  const R = 6371; // Earth radius, km
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
